import { formatAccountType } from '@/util/account-type-format';
import type { ClientAgreementsToSignRecord } from '@/api/custom';
import type { ApexAccount } from '@/types/wealth';
import type { components } from '@/util/apex-client-gen';

type ApexAgreement = components['schemas']['Agreement'];
export type AgreementStatus = 'signed' | 'not-signed' | 'voided' | 'unknown';

export type AgreementRow = {
  key: string;
  name: string;
  details: string;
  version?: string;
  status: AgreementStatus;
  signedOn?: string;
  url?: string;
};

export const STATUS_LABELS: Record<AgreementStatus, string> = {
  signed: 'Signed',
  'not-signed': 'Not signed',
  voided: 'Voided',
  unknown: '—',
};

const apexStatus = (state: ApexAgreement['agreement_state']): AgreementStatus => {
  if (state === 'AFFIRMED') return 'signed';
  if (state === 'REQUIRED') return 'not-signed';
  if (state === 'VOIDED') return 'voided';
  return 'unknown';
};

const accountDetails = (account: ApexAccount) => {
  const last4 = account.name?.slice(-4);
  const typeLabel = formatAccountType(account.type);
  return last4 ? `${typeLabel} ••${last4}` : typeLabel;
};

// Only open agreement documents over http(s).
const safeUrl = (value?: string | null) =>
  value && /^https?:\/\//i.test(value) ? value : undefined;

const toDateOnly = (value: unknown) =>
  typeof value === 'string' && value ? value.slice(0, 10) : undefined;

/**
 * Interim agreement list until signed records come from the backend
 * (ENG-3498): the firm's enabled agreements, which carry no signing record
 * yet, and each Apex account's agreements with their affirmation state.
 */
export const buildAgreementRows = (
  firmAgreements: ClientAgreementsToSignRecord[],
  apexAccounts: ApexAccount[],
): AgreementRow[] => [
  ...firmAgreements
    .filter((agreement) => agreement.enabled !== false)
    .map((agreement) => ({
      key: `firm-${agreement.agreement_id}`,
      name: agreement.title,
      details: agreement.description || 'Firm agreement',
      version: agreement.version || undefined,
      status: 'unknown' as const,
      url: safeUrl(agreement.url),
    })),
  ...apexAccounts.flatMap((account) =>
    (account.account_detail?.agreements ?? []).map((agreement, index) => {
      const status = apexStatus(agreement.agreement_state);
      return {
        key: `apex-${account.id}-${agreement.agreement_id ?? index}`,
        name: agreement.agreement_name || agreement.name || 'Account agreement',
        details: accountDetails(account),
        version: agreement.agreement_version || undefined,
        status,
        signedOn: status === 'signed' ? toDateOnly(agreement.affirmation_time) : undefined,
        url: safeUrl(agreement.agreement_uri),
      };
    }),
  ),
];
